import { useQuery } from '@tanstack/react-query'
import { Download, Smartphone, Code2, Package, Terminal, QrCode, ExternalLink, Zap, Share2, Shield } from 'lucide-react'

interface ManifestInfo {
  version: string
  download_url: string
  sha256: string
  changelog: string
  min_vscode?: string
  file_size: number
}

function formatSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`
  return `${bytes} B`
}

export default function CoherenceDownload() {
  const androidDownloadUrl = 'https://appstore.multiskilled.ai/api/apps/coherence-engine/download/1.0.0/android'
  const vsCodeUrl = 'https://appstore.multiskilled.ai/api/assets/extension/coherence-ai/latest'
  const pythonSdkUrl = 'https://appstore.multiskilled.ai/api/assets/sdk/coherence-sdk-python/latest'
  const cliUrl = 'https://appstore.multiskilled.ai/api/assets/cli/coherence-cli/latest'
  const dashboardUrl = 'https://coherence.multiskilled.ai'
  const apiDocsUrl = 'https://coherence.multiskilled.ai/docs'

  // Fetch live version info from AppStore manifest endpoints
  const { data: vsCodeManifest } = useQuery<ManifestInfo>({
    queryKey: ['manifest', 'extension', 'coherence-ai'],
    queryFn: async () => {
      const res = await fetch('/api/assets/extension/coherence-ai/manifest')
      if (!res.ok) return null
      return res.json()
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: sdkManifest } = useQuery<ManifestInfo>({
    queryKey: ['manifest', 'sdk', 'coherence-sdk-python'],
    queryFn: async () => {
      const res = await fetch('/api/assets/sdk/coherence-sdk-python/manifest')
      if (!res.ok) return null
      return res.json()
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: cliManifest } = useQuery<ManifestInfo>({
    queryKey: ['manifest', 'cli', 'coherence-cli'],
    queryFn: async () => {
      const res = await fetch('/api/assets/cli/coherence-cli/manifest')
      if (!res.ok) return null
      return res.json()
    },
    staleTime: 5 * 60 * 1000,
  })

  const vsCodeVersion = vsCodeManifest
    ? `v${vsCodeManifest.version} • ${formatSize(vsCodeManifest.file_size)}`
    : 'Latest'

  const sdkVersion = sdkManifest
    ? `v${sdkManifest.version} • ${formatSize(sdkManifest.file_size)}`
    : 'Latest'

  const cliVersion = cliManifest
    ? `v${cliManifest.version} • ${formatSize(cliManifest.file_size)}`
    : 'Latest'

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'AI Agent Orchestration',
      description: 'Coordinate multiple AI agents working together on complex tasks'
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: 'Knowledge Sharing',
      description: 'Share learnings and patterns across your entire organization'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Enterprise Ready',
      description: 'SSO, audit logs, and compliance features built-in'
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 via-purple-900/30 to-transparent" />

        <div className="relative max-w-4xl mx-auto text-center">
          {/* App Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="w-32 h-32 rounded-3xl shadow-2xl shadow-indigo-500/30 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="absolute -bottom-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                v1.0.0
              </div>
            </div>
          </div>

          {/* Title & Description */}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Coherence Engine
            </span>
          </h1>

          <p className="text-xl text-gray-300 mb-2">
            AI Orchestration Platform
          </p>

          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Enterprise AI orchestration for knowledge sharing, agent coordination, and ecosystem monitoring across all your projects.
          </p>
        </div>
      </section>

      {/* Mobile App Download */}
      <section className="py-12 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-green-400" />
            Mobile App
          </h2>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">Coherence Engine for Android</h3>
                <p className="text-gray-400 mb-6">Monitor agents, tasks, and AI insights on the go. Access your Coherence dashboard from anywhere.</p>

                <a
                  href={androidDownloadUrl}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 font-semibold transition-all shadow-lg shadow-green-500/30 hover:shadow-green-500/50"
                >
                  <Download className="w-5 h-5" />
                  Download APK (v1.0.0 • 73 MB)
                </a>
              </div>

              {/* QR Code */}
              <div className="flex-shrink-0 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <QrCode className="w-5 h-5 text-green-400" />
                  <span className="font-medium text-sm">Scan to Download</span>
                </div>
                <div className="bg-white p-3 rounded-xl inline-block">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent('https://appstore.multiskilled.ai/api/apps/coherence-engine/download/1.0.0/android')}`}
                    alt="QR Code"
                    className="w-28 h-28"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Android 7.0+</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Tools */}
      <section className="py-12 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
            <Code2 className="w-6 h-6 text-blue-400" />
            Developer Tools
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* VS Code Extension */}
            <a
              href={vsCodeUrl}
              className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all hover:translate-y-[-2px]"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                  <Code2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">VS Code Extension</h3>
                  <p className="text-sm text-gray-500">{vsCodeVersion}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                AI-powered development assistant in your IDE. Access agents, knowledge search, and monitoring directly in VS Code.
              </p>
              <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
                <Download className="w-4 h-4" />
                Download VSIX
              </div>
            </a>

            {/* Python SDK */}
            <a
              href={pythonSdkUrl}
              className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-yellow-500/50 transition-all hover:translate-y-[-2px]"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 bg-yellow-500/20 rounded-lg text-yellow-400">
                  <Package className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Python SDK</h3>
                  <p className="text-sm text-gray-500">{sdkVersion}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Official Python SDK for Coherence Engine API. Build AI-powered applications with full type hints and async support.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 text-sm font-medium">
                <Download className="w-4 h-4" />
                Download Package
              </div>
            </a>

            {/* CLI Tool */}
            <a
              href={cliUrl}
              className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-green-500/50 transition-all hover:translate-y-[-2px]"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 bg-green-500/20 rounded-lg text-green-400">
                  <Terminal className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">CLI Tool</h3>
                  <p className="text-sm text-gray-500">{cliVersion}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Command-line interface for Coherence Engine. Manage agents, projects, tasks, and deployments from your terminal.
              </p>
              <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <Download className="w-4 h-4" />
                Download Tool
              </div>
            </a>

            {/* API Documentation */}
            <a
              href={apiDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-purple-500/50 transition-all hover:translate-y-[-2px]"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                  <ExternalLink className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">API Documentation</h3>
                  <p className="text-sm text-gray-500">Interactive docs</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Complete REST API reference with interactive examples. Explore all endpoints and schemas for integration.
              </p>
              <div className="flex items-center gap-2 text-purple-400 text-sm font-medium">
                <ExternalLink className="w-4 h-4" />
                View Documentation
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Web Platform */}
      <section className="py-12 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8">Web Platform</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-indigo-500/50 transition-all"
            >
              <h3 className="font-semibold mb-2">Coherence Dashboard</h3>
              <p className="text-gray-400 text-sm mb-4">
                Access your full Coherence Engine dashboard with real-time monitoring, agent management, and insights.
              </p>
              <div className="flex items-center gap-2 text-indigo-400 text-sm font-medium">
                <ExternalLink className="w-4 h-4" />
                Launch Dashboard
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* MCP Integration */}
      <section className="py-16 px-4 border-t border-white/10 bg-gradient-to-r from-orange-900/20 via-purple-900/20 to-orange-900/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <Code2 className="w-8 h-8 text-orange-400" />
            MCP Server Integration
          </h2>
          <p className="text-gray-300 mb-8">
            Integrate Coherence Engine with Claude Desktop and other MCP clients for seamless AI-powered workflows.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Installation */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-green-400" />
                Installation
              </h3>
              <pre className="bg-black/50 rounded-lg p-4 text-sm overflow-x-auto text-green-300">
                <code>pip install coherence-mcp-server</code>
              </pre>
            </div>

            {/* Configuration */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Code2 className="w-5 h-5 text-blue-400" />
                Configuration
              </h3>
              <pre className="bg-black/50 rounded-lg p-4 text-sm overflow-x-auto text-gray-300 font-mono text-xs">
                <code>{`{
  "mcpServers": {
    "coherence": {
      "command": "python",
      "args": ["-m", "coherence.mcp"],
      "env": {
        "COHERENCE_API_URL":
          "https://coherence.multiskilled.ai",
        "COHERENCE_API_KEY":
          "your-api-key"
      }
    }
  }
}`}</code>
              </pre>
            </div>
          </div>

          {/* Available Tools */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-yellow-400" />
              Available Tools
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="text-indigo-300 font-mono"><code>agents.list</code> - List all agents</div>
                <div className="text-indigo-300 font-mono"><code>agents.get</code> - Get agent details</div>
                <div className="text-indigo-300 font-mono"><code>projects.list</code> - List projects</div>
                <div className="text-indigo-300 font-mono"><code>projects.get</code> - Get project details</div>
              </div>
              <div className="space-y-2">
                <div className="text-indigo-300 font-mono"><code>tasks.create</code> - Create task</div>
                <div className="text-indigo-300 font-mono"><code>tasks.list</code> - List tasks</div>
                <div className="text-indigo-300 font-mono"><code>learnings.search</code> - Search learnings</div>
                <div className="text-indigo-300 font-mono"><code>learnings.add</code> - Add learnings</div>
              </div>
            </div>
          </div>

          {/* Claude Desktop Usage */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Claude Desktop Usage</h3>
            <p className="text-gray-400 text-sm mb-3">After configuring the MCP server, use it in Claude:</p>
            <pre className="bg-black/50 rounded-lg p-4 text-sm overflow-x-auto text-gray-300">
              <code>{`@coherence agents list
@coherence projects get myproject
@coherence learnings search "authentication"
@coherence tasks create --project myproject`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Platform <span className="text-indigo-400">Features</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-indigo-500/50 transition-colors">
                <div className="text-indigo-400 mb-4">{feature.icon}</div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="py-8 px-4 border-t border-white/10 bg-black/20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            Coherence Engine v1.0.0 | Part of the MSKAI Ecosystem
          </p>
        </div>
      </section>
    </div>
  )
}
